<script>
function toDate(value) {
    if (value instanceof Date) {
        return new Date(value.getFullYear(), value.getMonth(), value.getDate())
    }
    if (typeof value === 'string' && value) {
        const parts = value.slice(0, 10).split('-').map(Number)
        if (parts.length === 3 && parts[0]) {
            return new Date(parts[0], parts[1] - 1, parts[2])
        }
    }
    const now = new Date()
    return new Date(now.getFullYear(), now.getMonth(), now.getDate())
}

function ymd(date) {
    const d = toDate(date)
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export default {
    name: 'DateStepper',
    props: {
        value: { type: [Date, String], default: null },
        min: { type: [Date, String], default: null },
        max: { type: [Date, String], default: null },
        disabledFn: { type: Function, default: null },
    },
    computed: {
        current() {
            return toDate(this.value)
        },
        isoDate() {
            return ymd(this.current)
        },
        minIso() {
            return this.min ? ymd(this.min) : null
        },
        maxIso() {
            return this.max ? ymd(this.max) : null
        },
        label() {
            return this.current.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
        },
        isToday() {
            return this.isoDate === ymd(new Date())
        },
    },
    methods: {
        isBlocked(date) {
            if (this.min && date < toDate(this.min)) {
                return true
            }
            if (this.max && date > toDate(this.max)) {
                return true
            }
            if (this.disabledFn) {
                return !!this.disabledFn(ymd(date), date)
            }
            return false
        },
        emit(date) {
            this.$emit('input', date)
        },
        step(delta) {
            const date = new Date(this.current)
            for (let i = 0; i < 400; i += 1) {
                date.setDate(date.getDate() + delta)
                if (this.max && date > toDate(this.max) && delta > 0) {
                    return
                }
                if (this.min && date < toDate(this.min) && delta < 0) {
                    return
                }
                if (!this.isBlocked(date)) {
                    this.emit(new Date(date))
                    return
                }
            }
        },
        pick(value) {
            if (!value) {
                return
            }
            const date = toDate(value)
            if (this.isBlocked(date)) {
                return
            }
            this.emit(date)
        },
        onCalSelect(ymdString) {
            this.pick(ymdString)
            if (this.$refs.picker) {
                this.$refs.picker.hide()
            }
        },
        goToday() {
            const now = new Date()
            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
            if (!this.isBlocked(today)) {
                this.emit(today)
            }
        },
    },
}
</script>

<template>
    <div class="stepper">
        <button type="button" class="stepper__btn" title="Vorheriger Tag" @click="step(-1)"><b-icon icon="chevron-left" /></button>
        <b-dropdown ref="picker" variant="link" no-caret toggle-class="stepper__pick" menu-class="stepper__menu" title="Datum auswählen">
            <template #button-content>
                <b-icon icon="calendar3" class="stepper__ico" />
                <span class="stepper__label">{{ label }}</span>
            </template>
            <b-calendar :value="isoDate" :min="minIso" :max="maxIso" :date-disabled-fn="disabledFn" hide-header block @selected="onCalSelect" />
        </b-dropdown>
        <button type="button" class="stepper__btn" title="Nächster Tag" @click="step(1)"><b-icon icon="chevron-right" /></button>
        <button type="button" class="stepper__today" :class="{ 'is-on': isToday }" @click="goToday">Heute</button>
    </div>
</template>

<style lang="scss" scoped>
.stepper {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    border: 1px solid var(--line-2);
    border-radius: var(--r-pill);
    padding: 3px;
    background: var(--panel);
}
.stepper__btn {
    border: 0;
    background: transparent;
    color: var(--muted);
    width: 30px;
    height: 30px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;

    &:hover {
        background: var(--panel-2);
        color: var(--ink);
    }
}
.stepper ::v-deep .stepper__pick {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    margin: 0;
    padding: 5px 12px;
    border: 0;
    border-radius: var(--r-pill);
    background: transparent;
    color: var(--ink);
    text-decoration: none;
    cursor: pointer;

    &:hover,
    &:focus {
        background: var(--panel-2);
        text-decoration: none;
    }
}
.stepper__ico {
    color: var(--teal);
    flex: none;
}
.stepper__label {
    font-family: var(--font-display);
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    font-size: 13px;
    color: var(--text-heading);
    white-space: nowrap;
}
.stepper ::v-deep .stepper__menu {
    padding: 8px;
    background: var(--panel);
    border: 1px solid var(--line-2);
    border-radius: var(--r-md);
    min-width: 0;
}
.stepper ::v-deep .b-calendar-grid {
    border: 0;
}
.stepper__today {
    border: 0;
    background: var(--panel-2);
    color: var(--muted);
    border-radius: var(--r-pill);
    font-family: var(--font-display);
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    font-size: 11px;
    padding: 5px 12px;

    &.is-on {
        background: var(--action);
        color: #fff;
    }
}
</style>
